import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAppDispatch } from "@/redux/hooks";
import { routes } from "@/routes/utils";
import {
  useGetCollectionDetailsQuery,
  useDownloadCollectionsExcel,
} from "@/services/report";
import { Badge } from "@/components/ui";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const useCollectionDetails = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  

  // ✅ Excel Download Query (React Query v5: enabled false)
  const { mutate: downloadExcel, isPending: isExcelDownloading } =
    useDownloadCollectionsExcel();
    
  const formatDate = (date : string | Date| undefined | null) => {
  if (!date) return undefined;
  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`; // YYYY-MM-DD
};


  

  // ✅ Fetch collection data
  const {
    data: collectionData,
    isLoading,
    error,
  } = useGetCollectionDetailsQuery({
    ...(filterData?.year && { year: filterData.year }),
    ...(filterData?.month && { month: filterData.month }),
    ...(filterData?.geoRegionId && { geoRegionId: filterData.geoRegionId }),
    ...(filterData?.schoolName && { schoolName: filterData.schoolName }),
    ...(filterData?.crmRH && { crmRH: filterData.crmRH }),
    ...(filterData?.crmRM && { crmRM: filterData.crmRM }),
    ...(filterData?.invoiceOwner && { invoiceOwner: filterData.invoiceOwner }),
    ...(filterData?.status && { status: filterData.status }),
    ...(filterData?.dueDays && { dueDays: filterData.dueDays }),
    ...(filterData?.invoiceGenerationDate && { 
        invoiceGenerationDate: formatDate(filterData.invoiceGenerationDate) 
  }),
  });

  const rawData = collectionData?.data?.items || [];

  // ✅ Use raw data directly (filtering is done server-side)
  const tableData = useMemo(() => {
    return rawData;
  }, [rawData]);

  const totalRecords = tableData.length;
  const totalPages = Math.ceil(totalRecords / pageSize);

  // ✅ Calculate total invoice amount
  const totalInvoiceAmount = useMemo(() => {
  const total = tableData.reduce(
    (sum, record) => sum + (record.invoiceAmount || 0),
    0
  );

  return total.toLocaleString("en-IN"); // Indian numbering system
}, [tableData]);


  // ✅ Table columns
  const columns = [
    { accessorKey: "schoolName", header: "School Name" },
    { accessorKey: "crmRHName", header: "CRM RH" },
    { accessorKey: "crmRMName", header: "CRM RM" },
    { accessorKey: "invoiceOwnerName", header: "Invoice Owner" },
    {
      accessorKey: "invoiceGenerationDate",
      header: "Invoice Date",
      accessorFn: (r: any) =>
        r.invoiceGenerationDate
          ? new Date(r.invoiceGenerationDate).toLocaleDateString("en-IN")
          : "—",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => (
        <Badge
          variant="outline"
          className={cn(
            row?.row?.original?.status === "Paid"
              ? "bg-green-100 text-green-700"
              : "bg-yellow-100 text-yellow-700",
            "rounded-[5px] px-3 py-1 border-none text-xs"
          )}
        >
          {row?.row?.original?.status}
        </Badge>
      ),
    },
    {
      accessorKey: "invoiceAmount",
      header: "Invoice Amount (₹)",
      accessorFn: (r: any) => `₹ ${r.invoiceAmount?.toLocaleString() || 0}`,
    },
    {
      accessorKey: "balanceAmount",
      header: "Balance (₹)",
      accessorFn: (r: any) => `₹ ${r.balanceAmount?.toLocaleString() || 0}`,
    },
    {
      accessorKey: "lastPaymentDate",
      header: "Paid Date",
      accessorFn: (r: any) =>
        r.lastPaymentDate
          ? new Date(r.lastPaymentDate).toLocaleDateString("en-IN")
          : "—",
    },
    {
      accessorKey: "lastPaymentFinanceConfirmation",
      header: "Finance Confirmation",
      accessorFn: (r: any) => (r.lastPaymentFinanceConfirmation ? "Yes" : "No"),
    },
    { accessorKey: "dueDays", header: "Due (Days)" },
    {
      accessorKey: "invoiceRemark",
      header: "Latest Invoice Remarks",
    },
    { accessorKey: "lastPaymentRemark", header: "Latest Payment Remarks" },
  ];

  // ✅ Filter handlers
  const onCancelFilter = () => setOpenFilter(false);
  const onFilterBtnClick = () => setOpenFilter(true);
  const onFilterApply = (newFilterData: any) => {
    setFilterData(newFilterData);
    setPageNumber(0);
    setOpenFilter(false);
  };
  const onClearFilter = () => {
    setFilterData(null);
    setPageNumber(0);
  };

  // ✅ Excel Download Handler
  const handleDownloadExcel = async () => {
    await downloadExcel();
  };

  return {
    columns,
    openDeleteDialog,
    setOpenDeleteDialog,
    onCancelFilter,
    onFilterBtnClick,
    onFilterApply,
    onClearFilter,
    openFilter,
    filterData,
    setFilterData,
    tableData,
    pageNumber,
    pageSize,
    totalPages,
    totalRecords,
    setPageNumber,
    setPageSize,
    isLoading,
    error,
    isDeleting: false,
    handleDownloadExcel,
    isExcelDownloading,
    totalInvoiceAmount,
  };
};

export default useCollectionDetails;
